import { useFormikContext } from 'formik'
import {
  FieldSpec,
  FieldSpecWithId,
  isFieldEnabled,
  makeFieldSpecMap,
} from 'lib/rich'
import React, { createContext, useContext, useMemo } from 'react'

type RichContextType = {
  fieldSpecs: readonly FieldSpecWithId[]
  data: any
  fieldSpecsMap: Record<string, FieldSpec>
  readOnly: boolean
  useV2: boolean
  narrowFieldWidth: number // px width for *narrow* fields
  metric: boolean
}

const RichContext = createContext<RichContextType | undefined>(undefined)

type RichProviderProps = React.PropsWithChildren<{
  fieldSpecs: readonly FieldSpecWithId[]
  data: any
  readOnly: boolean
  useV2: boolean
  narrowFieldWidth?: number // px width for *narrow* fields
  metric?: boolean
}>

export const RichContextProvider: React.FC<RichProviderProps> = ({
  children,
  fieldSpecs,
  data,
  readOnly = false,
  useV2 = false,
  narrowFieldWidth = 96, // px default width for narrow fields
  metric,
}) => {
  const fieldSpecsMap = useMemo(() => {
    return makeFieldSpecMap(fieldSpecs)
  }, [fieldSpecs])

  return (
    <RichContext.Provider
      value={{
        fieldSpecs,
        data,
        fieldSpecsMap,
        readOnly,
        useV2,
        narrowFieldWidth,
        metric: metric !== undefined ? metric : data?.unit === 'mm',
      }}>
      {children}
    </RichContext.Provider>
  )
}

export const useRichContext = () => {
  const context = useContext(RichContext)
  if (!context) {
    throw new Error('useRichContext must be used within a RichProvider')
  }
  return context
}

export function useRichField(name) {
  const { fieldSpecsMap, data } = useRichContext()
  const { values } = useFormikContext()

  const fieldSpec = fieldSpecsMap?.[name]
  const value = values?.[name]
  const enabled = fieldSpec ? isFieldEnabled(fieldSpec, values) : true

  return { name, fieldSpec, value, enabled }
}
